import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Typography,
  Button,
  useMediaQuery,
  Box,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

import {
  AddCircleOutlineOutlined,
  CancelRounded,
  Edit,
} from '@mui/icons-material';
import { useAccount } from '../../contexts/Account';
import categoryBenefitsMap from '../../utils/categoryBenefitsMap';
import CustomIcon from '../../icons/customIcon';
import ACButton from '../../components/ACButton';
import { benefitsUpdate } from '../../services/company_services';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import { SnackbarType } from '../../services/utils/types';

const useStyles = ({ isSM }) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: '80px',
  },
  text: {
    mx: '0px',
    px: { xxs: '60px', xs: '0px' },
  },
  listItem: {
    marginBottom: 12,
  },
  additionalBenefitsLeftContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  benefitsCategoryText: {
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '-0.43px',
    color: '#232B35',
    marginBottom: '11px',
    overflow: 'hidden',
  },
  button: {
    height: '20px',
    width: '20px',
    marginLeft: '3px',
  },
  iconbutton: {
    height: '17px',
    width: '17px',
  },
  actions: {
    minWidth: '46px',
  },
  mainButton: {
    width: '94px',
    height: '44px',
    borderRadius: '10px',
    letterSpacing: -0.43,
    fontStyle: 'normal',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '24px',
    backgroundColor: '#3171F6',
    color: 'white',
    marginLeft: '10px',
    marginTop: '10px',
    marginBottom: '10px',
    '&:hover': {
      backgroundColor: '#3171F6',
    },
    '&:disabled': {
      opacity: 0.5,
      backgroundColor: '#3171F6',
      color: 'white',
    },
  },
  secondaryButton: {
    width: '94px',
    height: '44px',
    letterSpacing: -0.43,
    fontStyle: 'normal',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '24px',
    color: '#646D7A',
    marginLeft: '10px',
    marginTop: '10px',
    marginBottom: '10px',
    '&:disabled': {
      opacity: 0.5,
      color: '#646D7A',
    },
  },
});

export default function step3() {
  const history = useHistory();
  const { accountDetails, asLoggedUserDetails, isImpersonated } = useAccount();
  const [benefits, setBenefits] = useState([
    {
      category: 'Advisory Board Affiliation',
      details:
        'Advisors may add their affiliation with this advisory board on their resume, biography and anywhere else',
      featured: false,
    },
    {
      category: 'Complimentary Product',
      details:
        'Advisors receive a complimentary product, service or gift certificate for being on this board at some specified point.',
      featured: false,
    },
    {
      category: 'Board Member Discount',
      details:
        'Advisors may receive a discount on particular products or services sold by this company.',
      featured: false,
    },
    {
      category: 'Advisor Rate Opportunity',
      details:
        'Advisors may be hired at their specified Advisor Rate if their ideas shared in this digital boardroom warrant additional insights or an additional project.',
      featured: false,
    },
    {
      category: '$1k Golden Idea Bonus',
      details:
        "If one of the ideas shared in this boardroom is deemed a “Golden Idea” by the board's leadership, they will receive an Advisor Bonus of $1,000",
      featured: false,
    },
    {
      category: '1% Equity Pledge',
      details:
        'Active advisors may be granted 1% equity on your next capital raise. (AdvisoryCloud will help with all the administration.)',
      featured: false,
    },
    {
      category: 'Perks',
      details:
        "Advisors may be invited to an event or special dinner hosted by the board's leadership, or a once a year meal or party you put on for your advisory board.",
      featured: false,
    },
    {
      category: 'Perks',
      details:
        'Advisors may receive a hat, plaque, letter of recommendation on their behalf, or a coffee/beer the next time the board meets in person!',
      featured: false,
    },
  ]);
  const [addBenefitModal, setAddBenefitModal] = useState(false);
  const [category, setCategory] = useState('');
  const [details, setDetails] = useState('');
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useStyles({ isSM });

  const openEditBenefit = (benefit, index) => {
    setAddBenefitModal(true);
    setCategory(benefit.category);
    setDetails(benefit.details);
    setSelectedBenefit(index);
  };

  const featuredBenefit = (index) => {
    setBenefits((prevBenefits) => {
      prevBenefits[index].featured = !prevBenefits[index].featured;
      return [...prevBenefits];
    });
  };

  const removeBenefit = (index) => {
    setBenefits((prevBenefits) => {
      return prevBenefits.filter((_, i) => i !== index);
    });
  };

  function addNewBenefit(benefit) {
    setBenefits((prevBenefits) => {
      if (typeof selectedBenefit === 'number') {
        prevBenefits[selectedBenefit] = {
          ...prevBenefits[selectedBenefit],
          ...benefit,
        };
        return [...prevBenefits];
      }
      return [...prevBenefits, benefit];
    });
  }

  function getIconByText(text) {
    const option =
      categoryBenefitsMap[text] || categoryBenefitsMap[text.toLowerCase()];
    return option ? option.icon : '';
  }

  function formatDetails(text) {
    var urlPattern = /(https?:\/\/[^\s]+)/g;
    var cleanedText = text.replace(urlPattern, '');

    cleanedText = cleanedText.substring(0, 250);

    return cleanedText;
  }

  async function handleSubmit() {
    try {
      setIsLoading(true);
      await benefitsUpdate({
        CONTACT_ID: asLoggedUserDetails.userId,
        COMPANY_BENEFITS: benefits.map((e) => JSON.stringify(e)),
      });
      history.push('/company/onboarding/step4');
    } catch (error) {
      console.log('Error updating benefits:', error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Box sx={classes.content}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          // maxWidth: '700px',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: isXS ? '24px' : '28px',
            fontWeight: 700,
            letterSpacing: '-0.43px',
            textAlign: 'center',
            my: '12px',
          }}
        >
          What perks would you like to offer your advisory board?
        </Typography>
        <Typography
          sx={{
            fontStyle: 'italic',
            fontSize: '16px',
            fontWeight: 300,
            lineHeight: '24px',
            letterSpacing: '-0.43px',
            color: '#646D7A',
            marginBottom: '18px',
          }}
        >
          The more perks, the more engaged they'll be! Use or edit the examples
          below, or add your own. You can edit them or add more at anytime in
          your boardroom.
        </Typography>
      </Box>
      <Grid
        container
        style={{
          marginBottom: '10px',
          justifyContent: 'center',
          maxWidth: '1035px',
        }}
      >
        {benefits.map((benefit, index) => (
          <div
            className="benefitItem"
            style={{
              border: '1px solid #EFF0F7',
              borderRadius: '12px',
              width: isXS ? '100%' : '330px',
              padding: '20px',
              margin: isXS ? '10px 5px' : '14px 15px 14px 0px',
            }}
            key={index}
          >
            <div style={classes.additionalBenefitsLeftContainer}>
              <div
                style={{
                  marginRight: '15px',
                  marginTop: '3px',
                }}
              >
                {getIconByText(benefit.category)}
              </div>
              <Typography sx={classes.benefitsCategoryText}>
                {benefit.category}
              </Typography>
              <div style={classes.actions}>
                <div>
                  <IconButton
                    sx={classes.button}
                    onClick={() => {
                      openEditBenefit(benefit, index);
                    }}
                  >
                    <Edit sx={classes.iconbutton} />
                  </IconButton>
                  <IconButton
                    sx={classes.button}
                    onClick={() => {
                      removeBenefit(index);
                    }}
                  >
                    <CancelRounded sx={classes.iconbutton} />
                  </IconButton>
                  {isImpersonated && (
                    <IconButton
                      sx={classes.button}
                      onClick={() => {
                        featuredBenefit(index);
                      }}
                    >
                      <CustomIcon
                        iconname={benefit.featured ? 'star' : 'star-outlined'}
                        style={{
                          fontSize: '18px',
                          marginTop: '-3px',
                        }}
                      />
                    </IconButton>
                  )}
                </div>
              </div>
            </div>
            <div style={classes.additionalBenefitsLeftContainer}>
              <Box sx={{ width: '100%' }}>
                <Typography sx={classes.benefitsDetailsText}>
                  {formatDetails(benefit.details)}
                </Typography>
              </Box>
            </div>
          </div>
        ))}
      </Grid>

      {addBenefitModal ? (
        <Box>
          <Grid container justifyContent="space-between">
            <Grid item sx={{ marginTop: '5px', marginBottom: '8px' }}>
              <Typography>
                Choose any benefits or compensation opportunities for advisors
              </Typography>
            </Grid>
          </Grid>
          <FormControl fullWidth>
            <InputLabel
              sx={{
                height: '44px',
                top: '-4px',
                fontFamily: 'Poppins',
                display: !category ? 'block' : 'none',
              }}
              id="demo-simple-select-label"
              variant="filled"
            >
              Select a benefits category
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={category || ''}
              placeholder="Select a benefits category"
              sx={{
                height: '44px',
                borderRadius: '10px',
              }}
              onChange={(e) => {
                setCategory(e.target.value);
              }}
            >
              {Object.entries(categoryBenefitsMap).map(
                ([key, { icon, label }]) => (
                  <MenuItem sx={classes.menuItem} key={label} value={label}>
                    {label}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <div>
            <TextField
              placeholder="A short description of your benefit is required. e.g. Offering equity awards for select members of our advisory board"
              variant="outlined"
              fullWidth
              multiline
              rows={5}
              style={{
                marginRight: 10,
                marginTop: 15,
              }}
              value={details || ''}
              inputProps={{
                maxLength: 250,
                style: {
                  borderRadius: '10px',
                },
              }}
              onChange={(evt) => setDetails(evt.target.value)}
            />
          </div>
          <Grid container justifyContent="flex-end">
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  addNewBenefit({ category, details });
                  setCategory('');
                  setDetails('');
                  setAddBenefitModal(false);
                  setSelectedBenefit(null);
                }}
                sx={classes.mainButton}
                disabled={!category || !details || details.length < 10}
              >
                Save
              </Button>
            </Box>
            <Box>
              <Button
                variant="text"
                color="primary"
                onClick={() => {
                  setCategory('');
                  setDetails('');
                  setAddBenefitModal(false);
                }}
                sx={classes.secondaryButton}
              >
                Discard
              </Button>
            </Box>
          </Grid>
        </Box>
      ) : null}
      {!addBenefitModal && (
        <div style={classes.addContentContainer}>
          <ACButton
            color="primary"
            size="medium"
            variant="text"
            style={{
              borderWidth: 2,
              borderRadius: 5,
              paddingLeft: 0,
            }}
            onClick={() => {
              setAddBenefitModal(true);
            }}
            disabled={isLoading}
          >
            <AddCircleOutlineOutlined />
            <Typography>Add a benefit</Typography>
          </ACButton>
        </div>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: '28px',
        }}
      >
        <Button
          id="company-onboarding-step-3"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          className="getStartedButton"
          disabled={isLoading}
          sx={{
            paddingTop: '15px',
            paddingBottom: '15px',
            textTransform: 'none',
            fontWeight: '700',
            fontSize: '15px',
            width: '260px',
            height: '44px',
            borderRadius: '30px',
          }}
        >
          {isLoading ? <LoadingLogoSkeleton /> : 'Finish!'}
        </Button>
      </Box>
      <ACSnackbar
        open={showSnackbar}
        text="Something went wrong"
        severity={SnackbarType.ERROR}
        onClose={() => {
          setShowSnackbar(false);
        }}
        autoHideDuration={5000}
      />
    </Box>
  );
}
